import type { BookingData } from '@/core/models/booking';
import { BookingStep } from '@/core/models/booking';

export const BOOKING_DRAFT_STORAGE_KEY = 'mindoasisBookingDraft';
export const BOOKING_DRAFT_VERSION = 1;
export const BOOKING_DRAFT_MAX_AGE_MS = 60 * 60 * 1000;

export type BookingDraft = {
  version: number;
  savedAt: number;
  country: string;
  step: BookingStep;
  bookingData: BookingData;
};

function isBrowser(): boolean {
  return typeof window !== 'undefined';
}

function normalizeCountry(country: string): string {
  return country.trim().toUpperCase();
}

export function hasBookingDraftSelection(bookingData: BookingData): boolean {
  return bookingData.location !== null || bookingData.mainService !== null;
}

function hasCustomerDetailsForSummary(bookingData: BookingData): boolean {
  const { firstName, lastName, email } = bookingData.customerDetails;
  return Boolean(firstName.trim() && lastName.trim() && email.trim());
}

function maxSupportedStep(
  bookingData: BookingData,
  isAuthenticated: boolean,
): BookingStep {
  if (!bookingData.location) return BookingStep.Location;
  if (!bookingData.mainService) return BookingStep.Service;
  if (!bookingData.date || !bookingData.timeSlot) return BookingStep.DateTime;

  if (isAuthenticated) return BookingStep.Summary;

  if (hasCustomerDetailsForSummary(bookingData) || bookingData.isGuestCheckout) {
    return BookingStep.Summary;
  }

  return BookingStep.Account;
}

export function resolveRestorableStep(
  step: BookingStep,
  bookingData: BookingData,
  isAuthenticated = false,
): BookingStep {
  const supportedStep = maxSupportedStep(bookingData, isAuthenticated);
  return Math.min(step, supportedStep);
}

function isValidBookingStep(step: unknown): step is BookingStep {
  if (typeof step !== 'number') return false;

  return (
    step >= Number(BookingStep.Location) && step <= Number(BookingStep.Summary)
  );
}

function isValidBookingData(value: unknown): value is BookingData {
  if (!value || typeof value !== 'object') return false;

  const data = value as Partial<BookingData>;
  return (
    (data.location === null || typeof data.location === 'object') &&
    (data.mainService === null || typeof data.mainService === 'object') &&
    (data.addOnService === null ||
      data.addOnService === undefined ||
      typeof data.addOnService === 'object') &&
    typeof data.guests === 'number' &&
    (data.date === null || typeof data.date === 'string') &&
    (data.timeSlot === null || typeof data.timeSlot === 'object') &&
    typeof data.customerDetails === 'object' &&
    data.customerDetails !== null &&
    typeof data.customerDetails.firstName === 'string' &&
    typeof data.customerDetails.lastName === 'string' &&
    typeof data.customerDetails.email === 'string' &&
    typeof data.customerDetails.phone === 'string' &&
    typeof data.isGuestCheckout === 'boolean' &&
    typeof data.notes === 'string' &&
    typeof data.totalPrice === 'number'
  );
}

function parseBookingDraft(raw: string): BookingDraft | null {
  try {
    const parsed = JSON.parse(raw) as Partial<BookingDraft>;
    if (parsed.version !== BOOKING_DRAFT_VERSION) return null;
    if (typeof parsed.savedAt !== 'number') return null;
    if (typeof parsed.country !== 'string' || !parsed.country.trim()) return null;
    if (!isValidBookingStep(parsed.step)) return null;
    if (!isValidBookingData(parsed.bookingData)) return null;

    return {
      version: parsed.version,
      savedAt: parsed.savedAt,
      country: normalizeCountry(parsed.country),
      step: parsed.step,
      bookingData: parsed.bookingData,
    };
  } catch {
    return null;
  }
}

function isExpired(savedAt: number, now = Date.now()): boolean {
  return now - savedAt > BOOKING_DRAFT_MAX_AGE_MS;
}

export function saveBookingDraft({
  country,
  step,
  bookingData,
}: {
  country: string;
  step: BookingStep;
  bookingData: BookingData;
}): void {
  if (!isBrowser()) return;
  if (!hasBookingDraftSelection(bookingData)) {
    clearBookingDraft();
    return;
  }

  const draft: BookingDraft = {
    version: BOOKING_DRAFT_VERSION,
    savedAt: Date.now(),
    country: normalizeCountry(country),
    step,
    bookingData,
  };

  sessionStorage.setItem(BOOKING_DRAFT_STORAGE_KEY, JSON.stringify(draft));
}

export function getBookingDraft(
  targetCountry: string,
  options?: { isAuthenticated?: boolean; now?: number },
): Pick<BookingDraft, 'step' | 'bookingData'> | null {
  if (!isBrowser()) return null;

  const raw = sessionStorage.getItem(BOOKING_DRAFT_STORAGE_KEY);
  if (!raw) return null;

  const draft = parseBookingDraft(raw);
  if (!draft) {
    clearBookingDraft();
    return null;
  }

  if (draft.country !== normalizeCountry(targetCountry)) {
    return null;
  }

  if (isExpired(draft.savedAt, options?.now)) {
    clearBookingDraft();
    return null;
  }

  if (!hasBookingDraftSelection(draft.bookingData)) {
    clearBookingDraft();
    return null;
  }

  const step = resolveRestorableStep(
    draft.step,
    draft.bookingData,
    options?.isAuthenticated ?? false,
  );

  return {
    step,
    bookingData: draft.bookingData,
  };
}

export function clearBookingDraft(): void {
  if (!isBrowser()) return;
  sessionStorage.removeItem(BOOKING_DRAFT_STORAGE_KEY);
}
